"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Menu, Bell } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex -mt-1">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent font-default text-sm font-medium text-foreground/90 hover:bg-accent/50 hover:text-primary focus:bg-accent/50 focus:text-primary data-[active=true]:bg-accent/50 data-[active=true]:text-primary transition-colors")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block px-4 py-3 text-lg font-default font-medium text-foreground hover:bg-accent/50 rounded-md transition-colors">
            {item.label}
          </Link>
        </div>)}
      <div className="mt-4 px-4">
        <Link to="#">
          <Button className="w-full font-default bg-primary text-primary-foreground hover:bg-primary/90" size="lg">
            <Bell className="mr-2 size-4" />
            Schedule a Consultation
          </Button>
        </Link>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264684" id="header" className="text-foreground sticky top-0 z-50 w-full transition-all duration-300 bg-background border-b border-transparent !h-[140px]">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative flex h-32 items-center justify-between gap-4">
          {}
          <div className="flex-shrink-0 flex !justify-center !items-start !w-auto !h-auto">
            <Link to="/HomePage" className="flex !items-center !w-[150px]">
             <WvcLogo className="text-primary w-[150px] max-w-[150px] [&_svg]:!w-full [&_svg]:!max-w-[150px] [&_svg]:!h-auto" />
            </Link>
          </div>

          {}
          <div className="flex-1 flex flex-col items-center justify-center gap-1">
            <MenuProvider menu_id="16">
              <DesktopNav />
            </MenuProvider>
            
          </div>

          {}
          <div className="hidden lg:flex items-center flex-shrink-0">
            <Link to="/StartLearningPage">
              <Button variant="default" className="font-default bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg relative overflow-hidden group rounded-md">
                <span className="absolute inset-0 w-full h-full bg-white/10 group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 -ml-4" />
                <Bell className="mr-2 size-4" />
                Schedule a Consultation
              </Button>
            </Link>
          </div>

          {}
          <div className="lg:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/50">
                  <Menu className="size-6" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background text-foreground border-border w-[300px] sm:w-[400px]">
                <div className="mt-6">
                  <Link to="/HomePage" className="inline-block mb-6">
                    <WvcLogo className="h-20 w-auto text-primary" />
                  </Link>
                  <MenuProvider menu_id="16">
                    <MobileNav />
                  </MenuProvider>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}